import { useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { FormField } from "@/pages/ContactPage";
import { SERVICE_OPTIONS } from "@/lib/content";
import { CheckCircle2 } from "lucide-react";

export default function QuotePage() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    service_type: SERVICE_OPTIONS[0],
    property_count: 1,
    coverage_area: "",
    notes: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/quote", { ...form, property_count: Number(form.property_count) || 1 });
      setDone(true);
      toast.success("Quote request received. We'll be in touch within 24 business hours.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-16 md:py-20 grid md:grid-cols-12 gap-12">
      <div className="md:col-span-5">
        <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500">Request a quote</div>
        <h1 className="font-display text-4xl md:text-5xl tracking-tighter font-semibold mt-3">
          Tell us about your portfolio.
        </h1>
        <p className="mt-6 text-slate-600 leading-relaxed">
          Provide the service scope, expected volume, and coverage area. Our
          operations team returns most quotes within 24 business hours with an
          itemized breakdown and SLA proposal.
        </p>
        <ol className="mt-10 space-y-4 text-sm">
          {["Submit request", "Scoping call (30 min)", "Itemized bid delivered", "Portal onboarding"].map((step, i) => (
            <li key={step} className="flex items-start gap-4">
              <div className="w-8 h-8 flex items-center justify-center bg-[#0B192C] text-white font-mono-alt text-xs">
                0{i + 1}
              </div>
              <div className="pt-1.5 text-slate-700">{step}</div>
            </li>
          ))}
        </ol>
      </div>
      <div className="md:col-span-7">
        {done ? (
          <div data-testid="quote-success" className="border border-emerald-200 bg-emerald-50 p-8">
            <CheckCircle2 className="h-6 w-6 text-emerald-700" />
            <div className="font-display text-2xl mt-3 tracking-tight">Quote request received.</div>
            <p className="text-sm text-slate-600 mt-2">
              A member of our operations team will follow up within 24 business
              hours.
            </p>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="quote-form" className="border border-slate-200 bg-white p-6 md:p-8 grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField label="Full name" required id="q-name" value={form.name} onChange={update("name")} testId="quote-name" />
            <FormField label="Email" required id="q-email" type="email" value={form.email} onChange={update("email")} testId="quote-email" />
            <FormField label="Company" id="q-company" value={form.company} onChange={update("company")} testId="quote-company" />
            <FormField label="Phone" id="q-phone" value={form.phone} onChange={update("phone")} testId="quote-phone" />

            <label className="block">
              <span className="block text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-1.5">
                Primary service <span className="text-[#1E40AF] ml-1">*</span>
              </span>
              <select
                data-testid="quote-service"
                required
                value={form.service_type}
                onChange={update("service_type")}
                className="w-full px-3 py-2 bg-white border border-slate-300 focus:border-[#1E40AF] outline-none text-sm rounded-sm"
              >
                {SERVICE_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </label>
            <FormField label="Approx. property count" id="q-count" type="number" min={1} value={form.property_count} onChange={update("property_count")} testId="quote-count" />

            <div className="md:col-span-2">
              <FormField label="Coverage area (states / counties)" id="q-coverage" value={form.coverage_area} onChange={update("coverage_area")} testId="quote-coverage" />
            </div>
            <div className="md:col-span-2">
              <FormField label="Additional notes" id="q-notes" as="textarea" rows={4} value={form.notes} onChange={update("notes")} testId="quote-notes" />
            </div>

            <div className="md:col-span-2 flex items-center justify-between">
              <div className="text-xs text-slate-500">By submitting you agree to be contacted about this request.</div>
              <button
                data-testid="quote-submit-btn"
                type="submit"
                disabled={loading}
                className="px-5 py-2.5 bg-[#1E40AF] hover:bg-[#1E3A8A] text-white text-sm font-semibold rounded-sm disabled:opacity-60"
              >
                {loading ? "Submitting…" : "Request quote"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
